import { PlayExperience } from 'src/pages/dashboard/playExperiences'
import React from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { motion, Variants } from 'framer-motion'
import Grid from '@mui/material/Grid'
import ModeSelectView from './ModeSelectView'
import DashboardPlayTabView from './DashboardPlayTabView'
import DashboardGameViews, { DashboardGameViewsProps } from './DashboardGameViews'
import DashboardPollViews, { DashboardPollViewsProps } from './DashboardPollViews'
import { List } from 'src/api/types/lists'
import { Poll } from 'src/api/types/polls'
import { UserState } from 'src/app/AppContext'
import { GameSounds } from 'src/ui/sound/types'
import { AlertInput } from '@components/common/Alert'
import { Item } from '@components/common/ItemGuessBox'
import { MatchmakingStats } from 'src/api/types/matchmaking'
import { UserSoloStats } from 'src/api/types/users'
import type { Player } from 'src/pages/Dashboard'
import EditListView from 'src/sections/EditListView'

export type DashboardContentViewProps = {
  booted: boolean
  viewKey: string
  viewVariants: Variants
  gameState: string
  viewMode: string
  isPollDetailView: boolean
  isPollPlayView: boolean
  selectedTab: string
  showModeSelectOnly: boolean
  mode: 'single' | 'multi' | 'host' | undefined
  fullScreen: boolean
  multiMode: string
  selectedListTab: string
  sounds: GameSounds
  handleSelectTab: (tab: string) => void
  setSelectedTab: (tab: string) => void
  setSelectedList: (value: number | undefined) => void
  setActivePollId: (value: number | undefined) => void
  experience?: PlayExperience
  onSelectExperience: (experience: PlayExperience) => void
  dailyList: List | undefined
  soloStats?: UserSoloStats
  onStartGame: (listId?: number) => void
  user: UserState
  pollRefreshKey: number
  onPollSelect: (poll: Poll) => void
  onPollVote: (poll: Poll, hasVoted: boolean) => void
  onPollPlay: (poll: Poll) => void
  canCreatePoll: boolean
  setTopic: (value: string) => void
  setListItems: (items: Item[]) => void
  onStartRandomList: () => void
  onStartRandomPoll: () => void
  onStartDailyPoll: () => void
  onStartPreviousPoll: () => void
  onStartCreatedList: (payload: { topic: string; items: Item[]; listId?: number }) => void
  onAlert: (value: AlertInput) => void
  appUserId?: number
  onEditList?: (list: List) => void
  editList?: List
  listSearchShowMine?: boolean
  onListSearchShowMine?: (value: boolean) => void
  onBackFromListEdit?: () => void
  handleMakeMatch: () => void
  handleCreateLobby: (profile?: { name?: string; icon?: string }) => void
  handleJoinLobby: (profile?: { name?: string; icon?: string }) => void
  onChangeJoinCode: (value: string) => void
  onlineProfileName: string
  onlineProfileIcon: string
  canEditOnlineName: boolean
  onChangeOnlineName: (name: string) => void
  players: Player[]
  connectionId: string
  hostId: string
  onChangeOnlineTeam: (team: number) => void
  onChangeOnlineIcon: (icon: string) => void
  onCommitOnlineName: () => void
  onStartOnlineLobbyGame: () => void
  onCloseLobby: () => void
  onLeaveLobby: () => void
  onlineUsersAll: number
  onlineUsersLogged: number
  searchingRanked: number
  searchingUnranked: number
  matchmakingRated: boolean
  onSetMatchmakingRated: (value: boolean) => void
  joinGameCode: string
  leaderboard: MatchmakingStats[]
  myStats?: MatchmakingStats
  onlineMatchRated?: boolean
  onlineMatchType?: 'matchmaking' | 'lobby'
  pendingMatch: boolean
  gameCode?: string
  ws?: WebSocket
  onSetSelectedListTab: (tab: string) => void
  onPollCreated: (
    pollId: number,
    pollFormat?: 'ranked_options' | 'community_answers'
  ) => void
  gameViewProps: DashboardGameViewsProps
  pollViewProps: DashboardPollViewsProps
  onSelectModeClick: () => void
  onOpenHowToPlay: () => void
  onPollBack: () => void
  onNewPoll: () => void
}

const DashboardContentView: React.FC<DashboardContentViewProps> = (props) => {
  const {
    booted,
    viewKey,
    viewVariants,
    gameState,
    viewMode,
    isPollDetailView,
    isPollPlayView,
    selectedTab,
    showModeSelectOnly,
    mode,
    fullScreen,
    multiMode,
    selectedListTab,
    sounds,
    handleSelectTab,
    setSelectedTab,
    setSelectedList,
    setActivePollId,
    experience,
    onSelectExperience,
    dailyList,
    soloStats,
    onStartGame,
    user,
    pollRefreshKey,
    onPollSelect,
    onPollVote,
    onPollPlay,
    canCreatePoll,
    setTopic,
    setListItems,
    onStartRandomList,
    onStartRandomPoll,
    onStartDailyPoll,
    onStartPreviousPoll,
    onStartCreatedList,
    onAlert,
    appUserId,
    onEditList,
    editList,
    listSearchShowMine,
    onListSearchShowMine,
    onBackFromListEdit,
    handleMakeMatch,
    handleCreateLobby,
    handleJoinLobby,
    onChangeJoinCode,
    onlineProfileName,
    onlineProfileIcon,
    canEditOnlineName,
    onChangeOnlineName,
    players,
    connectionId,
    hostId,
    onChangeOnlineTeam,
    onChangeOnlineIcon,
    onCommitOnlineName,
    onStartOnlineLobbyGame,
    onCloseLobby,
    onLeaveLobby,
    onlineUsersAll,
    onlineUsersLogged,
    searchingRanked,
    searchingUnranked,
    matchmakingRated,
    onSetMatchmakingRated,
    joinGameCode,
    leaderboard,
    myStats,
    onlineMatchRated,
    onlineMatchType,
    pendingMatch,
    gameCode,
    ws,
    onSetSelectedListTab,
    onPollCreated,
    gameViewProps,
    pollViewProps,
    onSelectModeClick,
    onOpenHowToPlay,
    onPollBack,
    onNewPoll,
  } = props

  return (
    <Box sx={{ position: 'relative', minHeight: '50vh' }}>
      {/* Render the current state immediately; gameplay must never wait on an outgoing view. */}
      <React.Fragment>
        {!booted ? (
          <motion.div
            key="view-loader"
            variants={viewVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{ width: '100%' }}
          >
            <Box
              sx={{
                p: { xs: 4, md: 6 },
                textAlign: 'center',
                minHeight: { xs: '45vh', md: '40vh' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={56} />
            </Box>
          </motion.div>
        ) : (
          <motion.div
            key={viewKey}
            variants={viewVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{ width: '100%' }}
          >
            {showModeSelectOnly ? (
              <Box>
                <ModeSelectView
                  experience={experience}
                  onSelectExperience={onSelectExperience}
                  onOpenHowToPlay={onOpenHowToPlay}
                />
              </Box>
            ) : gameState === 'pre' &&
              !isPollDetailView &&
              !isPollPlayView &&
              viewMode !== 'list_edit' ? (
              <Box>
                {selectedTab === 'play' ? (
                  <>
                    <Grid container justifyContent="center" spacing={1}>
                      <Grid item xs={12} mt={0}>
                        <Grid container justifyContent="center" spacing={2}>
                          <Grid item xs={12}>
                            <DashboardPlayTabView
                              selectedListTab={selectedListTab}
                              onTabChange={handleSelectTab}
                              sounds={sounds}
                              fullScreen={fullScreen}
                              dailyList={dailyList}
                              soloStats={soloStats}
                              experience={experience}
                              mode={mode}
                              multiMode={multiMode}
                              onSelectModeClick={onSelectModeClick}
                              onOpenHowToPlay={onOpenHowToPlay}
                              setSelectedTab={setSelectedTab}
                              setSelectedList={setSelectedList}
                              setActivePollId={setActivePollId}
                              setTopic={setTopic}
                              setListItems={setListItems}
                              onStartGame={onStartGame}
                              onStartRandomList={onStartRandomList}
                              onStartRandomPoll={onStartRandomPoll}
                              onStartDailyPoll={onStartDailyPoll}
                              onStartPreviousPoll={onStartPreviousPoll}
                              userId={user?.id}
                              pollRefreshKey={pollRefreshKey}
                              onPollSelect={onPollSelect}
                              onPollVote={onPollVote}
                              onPollPlay={onPollPlay}
                              canCreatePoll={canCreatePoll}
                              appUserId={appUserId}
                              onPollCreated={onPollCreated}
                              onSetSelectedListTab={onSetSelectedListTab}
                              onStartCreatedList={onStartCreatedList}
                              onAlert={onAlert}
                              onEditList={onEditList}
                              listSearchShowMine={listSearchShowMine}
                              onListSearchShowMine={onListSearchShowMine}
                              onlineUsersAll={onlineUsersAll}
                              onlineUsersLogged={onlineUsersLogged}
                              searchingRanked={searchingRanked}
                              searchingUnranked={searchingUnranked}
                              matchmakingRated={matchmakingRated}
                              onSetMatchmakingRated={onSetMatchmakingRated}
                              joinGameCode={joinGameCode}
                              leaderboard={leaderboard}
                              myStats={myStats}
                              onlineMatchRated={onlineMatchRated}
                              onlineMatchType={onlineMatchType}
                              pendingMatch={pendingMatch}
                              handleMakeMatch={handleMakeMatch}
                              handleCreateLobby={handleCreateLobby}
                              handleJoinLobby={handleJoinLobby}
                              onChangeJoinCode={onChangeJoinCode}
                              onlineProfileName={onlineProfileName}
                              onlineProfileIcon={onlineProfileIcon}
                              canEditOnlineName={canEditOnlineName}
                              onChangeOnlineName={onChangeOnlineName}
                              players={players}
                              connectionId={connectionId}
                              hostId={hostId}
                              onChangeOnlineTeam={onChangeOnlineTeam}
                              onChangeOnlineIcon={onChangeOnlineIcon}
                              onCommitOnlineName={onCommitOnlineName}
                              onStartOnlineLobbyGame={onStartOnlineLobbyGame}
                              onCloseLobby={onCloseLobby}
                              onLeaveLobby={onLeaveLobby}
                              gameCode={gameCode}
                              ws={ws}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                ) : null}
              </Box>
            ) : null}

            {!showModeSelectOnly && viewMode === 'list_edit' && editList ? (
              <Box>
                <EditListView
                  list={editList}
                  onClose={onBackFromListEdit}
                  onDeleted={() => onBackFromListEdit?.()}
                  onOpenHowToPlay={onOpenHowToPlay}
                />
              </Box>
            ) : null}

            {!showModeSelectOnly && selectedTab === 'myGames' ? (
              <Box>
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                  }}
                  py={2}
                >
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h6" textAlign={'center'}>
                        My Games
                      </Typography>
                      <Typography variant="body2" textAlign={'center'} color={'gray'}>
                        Games are a work in progress. Only online games will be saved and
                        displayed.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ) : null}

            {/* Keep game state mounted while the pending poll view owns the visible UI. */}
            <DashboardGameViews {...gameViewProps} hidden={!!pollViewProps.pollLoading} />

            <DashboardPollViews
              {...pollViewProps}
              onPollBack={onPollBack}
              onNewPoll={onNewPoll}
              onOpenHowToPlay={onOpenHowToPlay}
            />
          </motion.div>
        )}
      </React.Fragment>
    </Box>
  )
}

export default DashboardContentView
